import React, { useState, useContext, useEffect } from "react";
import { Form, Col, Container, Dropdown } from "react-bootstrap";
import {
  Badge,
  Chip,
  Tooltip
} from "@material-ui/core";
import _ from "lodash";
import Moment from "moment";
import { ReactComponent as Filter } from "../../assets/icons/filter.svg";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { DashboardContext } from "../../context/DashboardContext";
import PopoverFilter from "./PopoverFilter";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import Info from "@material-ui/icons/InfoOutlined";
import FilterUtils from "../../utils/FilterUtils";
import DashboardMode from "../../constants/DashboardMode";
import { UserContext } from "../../context/UserContext";
import "./filters.scss";
import {
  StyledBlueButton,
  StyledDarkBlueButton,
} from "../../styles/BootstrapStyles";
import { useStyles } from "./Filters.styles";
import { SearchFieldsMap } from "../../constants/SearchFields";
import { UserRoles } from "../../constants/UserRoles";
import { DATE_TYPE_OPTIONS } from "../../constants/DateConstants";

const DATE_FORMAT = "YYYY-MM-DD";

export const getDates = (
  dateRange,
  toDate,
  fromDate,
  searchText,
  query,
  mode
) => {
  let dates = {};

  switch (dateRange) {
    case "lastFortyEight":
      let toTime = Moment().endOf("day");
      let fromTime = Moment().subtract(2, "days");
      fromTime = fromTime.startOf("day");
      dates = {
        fromDate: fromTime.toISOString(),
        toDate: toTime.toISOString()
      };
      break;
    case "lastWeek":
      let weekStartDate = Moment().subtract(1, "week");
      weekStartDate = weekStartDate.startOf("day");
      let weekEndDate = Moment().endOf("day");
      dates = {
        fromDate: weekStartDate.toISOString(),
        toDate: weekEndDate.toISOString()
      };
      break;
    case "lastMonth":
      let monthStartDate = Moment().subtract(1, "month");
      monthStartDate = monthStartDate.startOf("day");
      let monthEndDate =  Moment().endOf("day");
      dates = {
        fromDate: monthStartDate.toISOString(),
        toDate: monthEndDate.toISOString()
      };
      break;
    case "lastYear":
      let yearStartDate = Moment().subtract(1, "year");
      yearStartDate = yearStartDate.startOf("day");
      let yearEndDate = Moment().endOf("day");
      dates = {
        fromDate: yearStartDate.toISOString(),
        toDate: yearEndDate.toISOString(),
      };
      break;
    case "custom":
      let customStartDate = Moment(fromDate).isBefore(toDate) ? fromDate : toDate;
      customStartDate = Moment(customStartDate).startOf("day");
      let customEndDate =  Moment(toDate).isBefore(fromDate) ? fromDate : toDate;
      customEndDate = Moment(customEndDate).endOf("day");
      dates = {
        fromDate: customStartDate.toISOString(),
        toDate: customEndDate.toISOString(),
      };
      break;
    default:
      // All
      dates = {};
  }

  let _fromDate = _.get(dates, "fromDate");
  let _toDate = _.get(dates, "toDate");
  let finalQuery = {
    fromDate: _fromDate,
    toDate: _toDate,
    textSearch: searchText,
  };
  if (_.isEmpty(finalQuery.fromDate)) {
    delete finalQuery["fromDate"];
  }
  if (_.isEmpty(finalQuery.toDate)) {
    delete finalQuery["toDate"];
  }
  if (_.isEmpty(finalQuery.textSearch)) {
    delete finalQuery["textSearch"];
  }
  let clonedQuery = { ...query };
  if (_.isEmpty(clonedQuery[mode])) {
    clonedQuery[mode] = {};
  }

  let oldQuery = clonedQuery[mode];
  return {
    updatedQuery: Object.assign(oldQuery, finalQuery),
    finalQuery: finalQuery,
  };
};

const updateFilter = (
  dateRange: string,
  fromDate: string,
  toDate: string,
  searchText: string,
  query: {},
  mode: string,
  dateTypeKey: string,
  _setFilter: (query: any) => void,
  _setDateType: (view: string ) => void,
  showLoading: () => void
) => {
  let queries = getDates(dateRange, toDate, fromDate, searchText, query, mode);
  const options = DATE_TYPE_OPTIONS[mode] || [];
  const selectedOption = options.find(opt => opt.key === dateTypeKey);
  const dateField = selectedOption?.field;
  let updatedQuery = queries["updatedQuery"];
  let finalQuery = queries["finalQuery"];
  if (dateField) {
    updatedQuery["dateField"] = dateField
  }
  if (dateRange === "all") {
    delete updatedQuery["fromDate"];
    delete updatedQuery["toDate"];
    delete updatedQuery["dateField"];
    _setDateType("SUBMITTED_DATE");
  }
  if (_.isEmpty(finalQuery.textSearch)) {
    delete updatedQuery["textSearch"];
  }
  _setFilter(updatedQuery);
  showLoading();
};

const CustomFilters = () => {
  const [view, setView] = useState(null);
  const { filter, _setFilter, dashboardView, showLoading, jobDateType, 
    requestDateType, _setJobDateType, _setRequestDateType } =
    useContext(DashboardContext);
  const { filterList, userRole } = useContext(UserContext);
  const [query, setQuery] = useState({});
  const [dateRange, setDateRange] = useState("all");
  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [isDateTypeOpen, setIsDateTypeOpen] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    let _filter = JSON.parse(JSON.stringify(filter));
    setQuery(_filter);
    let fromDateValue = _.get(
      _.get(_filter, dashboardView, {}),
      "fromDate",
      ""
    );
    let toDateValue = _.get(_.get(_filter, dashboardView, {}), "toDate", "");
    let textSearch = _.get(_.get(_filter, dashboardView, {}), "textSearch", "");
    if (_.isEmpty(fromDateValue)) {
      setDateRange("all");
    } else {
      setDateRange("custom");
    }
    setToDate(
      _.isEmpty(toDateValue)
        ? Moment().format(DATE_FORMAT)
        : Moment(toDateValue).format(DATE_FORMAT)
    );
    setFromDate(
      _.isEmpty(fromDateValue)
        ? Moment().subtract(1, "month").format(DATE_FORMAT)
        : Moment(fromDateValue).format(DATE_FORMAT)
    );
    if (!_.isEmpty(textSearch)) {
      setSearchText(textSearch);
    } else {
      setSearchText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    let _filter = JSON.parse(JSON.stringify(filter));
    setQuery(_filter);
    let fromDateValue = _.get(
      _.get(_filter, dashboardView, {}),
      "fromDate",
      ""
    );
    let toDateValue = _.get(_.get(_filter, dashboardView, {}), "toDate", "");
    let textSearch = _.get(_.get(_filter, dashboardView, {}), "textSearch", "");
    if (_.isEmpty(fromDateValue)) {
      setDateRange("all");
    } else {
      setDateRange("custom");
    }
    setToDate(
      _.isEmpty(toDateValue)
        ? Moment().format(DATE_FORMAT)
        : Moment(toDateValue).format(DATE_FORMAT)
    );
    setFromDate(
      _.isEmpty(fromDateValue)
        ? Moment().subtract(1, "month").format(DATE_FORMAT)
        : Moment(fromDateValue).format(DATE_FORMAT)
    );
    if (!_.isEmpty(textSearch)) {
      setSearchText(textSearch);
    } else {
      setSearchText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardView]);

  const handleClick = (e, value) => {
    setDateRange(value);

    switch (value) {
      case "lastFortyEight":
        var toTime = Moment().endOf("day");
        var fromTime = Moment().subtract(2, "days");
        fromTime = fromTime.startOf("day");
        var fromDate1 = fromTime.utc().format();
        var toDate1 = toTime.utc().format();
        setFromDate(Moment(fromDate1).format(DATE_FORMAT));
        setToDate(Moment(toDate1).format(DATE_FORMAT));
        break;
      case "lastWeek":
        setFromDate(Moment().subtract(1, "week").format(DATE_FORMAT));
        setToDate(Moment().format(DATE_FORMAT));
        break;
      case "lastMonth":
        setFromDate(Moment().subtract(1, "month").format(DATE_FORMAT));
        setToDate(Moment().format(DATE_FORMAT));

        break;
      case "lastYear":
        setFromDate(Moment().subtract(1, "year").format(DATE_FORMAT));
        setToDate(Moment().format(DATE_FORMAT));

        break;
      default:
        // All
        _setJobDateType("SUBMITTED_DATE");
        _setRequestDateType("SUBMITTED_DATE");
        break;
    }
  };

  const renderUserFilters = (role, filterParams) => {
    let filtersArray = [];
    _.map(filterParams, (filterProp) => {
      if (_.indexOf(filterProp.permissions, role) !== -1) {
        if (filterProp.name === "status") {
          let updatedFilter = { ...filterProp };
          if (role === UserRoles.ADMIN && dashboardView === DashboardMode.REQUESTS) {
            // Don't update filter
          } else {
            updatedFilter.values = _.reject(
              updatedFilter.values,
              (item) => item.value === "DRAFT"
            );
          }
          filtersArray.push(
            renderFilter(
              updatedFilter.name,
              updatedFilter.label,
              updatedFilter.values,
              updatedFilter.menuPlacement,
              true
            )
          );
        } else if(filterProp.name === "requestor.deptName" && role === "requestor") {
          filtersArray.push(
            renderFilter(
              filterProp.name,
              filterProp.label,
              filterProp.values,
              filterProp.menuPlacement,
              true
            )
          );
        } else {
          filtersArray.push(
            renderFilter(
              filterProp.name,
              filterProp.label,
              filterProp.values,
              filterProp.menuPlacement
            )
          );
        }
      }
    });
    return filtersArray;
  };

  const renderFilter = (
    filterKey,
    title,
    items,
    menuPlacement,
    isArrayQuery = false
  ) => {
    return (
      <Form.Group
        as={Col}
        controlId={filterKey}
        key={filterKey}
        className="filterFormGroup"
        style={{
          maxWidth: dateRange === "all" ? "inherit" : 250,
        }}
      >
        <label
          onClick={(e) => {
            e.preventDefault();
            setView({
              target: e.currentTarget,
              title: title,
              items: items,
              key: filterKey,
              isArrayQuery,
            });
          }}
          key={"custom_filter_" + title}
          style={{ cursor: "pointer" }}
          className="filterInput"
        >
          <span
            className={
              !_.isEmpty(_.get(_.get(query, dashboardView, {}), filterKey))
                ? "selectedFilterSpan"
                : "filterSpan"
            }
          >
            {title}
          </span>
          <span>
            {_.get(view, "title", "") === title ? (
              <ArrowDropUpIcon />
            ) : (
              <ArrowDropDownIcon />
            )}
          </span>
        </label>
      </Form.Group>
    );
  };

  const getFilterByKey = (mode, key) => {
    return _.get(_.get(query, mode, {}), key, []);
  };
  const deleteValue = (name, key) => {
    let clonnedQuery = { ...query };
    if (_.isEmpty(clonnedQuery[dashboardView])) {
      clonnedQuery[dashboardView] = {};
    }
    let dataValue: string | Array<string> = "";
    try {
      dataValue = clonnedQuery[dashboardView][key];
    } catch (e) {}
    let data = _.isEmpty(dataValue)
      ? []
      : dataValue instanceof Array
      ? dataValue
      : dataValue.split(",");
    let index = data.indexOf(name);
    if (index > -1) {
      data.splice(index, 1);
    }

    clonnedQuery[dashboardView][key] = data.join(",");
    if (_.isEmpty(data)) {
      delete clonnedQuery[dashboardView][key];
    }
    setQuery(clonnedQuery);
  };

  const renderPresetFilter = (label) => {
    return (
      <>
        <span>Preset filter</span>
        <br></br>
        {label}
      </>
    );
  };

  let updatedFilters = FilterUtils.filterView(dashboardView, filterList, userRole);
  let filters = renderUserFilters(userRole, updatedFilters);

  useEffect(() => {
    if (view && view.items.length === 0) {
      const targetFilter = _.find(
        updatedFilters,
        (filterObj) => filterObj.name === view.key
      );
      setView({
        ...view,
        items: targetFilter.values,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const renderDateTypeDropdown = (
    DATE_TYPE_OPTIONS,
    dashboardView,
    dateTypeKey,
    setDateTypeKey,
    isDateTypeOpen,
    setIsDateTypeOpen
  ) => {
    const options = DATE_TYPE_OPTIONS[dashboardView];
    const selectedOption = options.find(opt => opt.key === dateTypeKey);

    return (
      <Form.Group
        as={Col}
        controlId="dateType"
        className="dateRangeFilterFormGroup dateTypeForm"
      >
        <Dropdown
          show={isDateTypeOpen}
          onToggle={setIsDateTypeOpen}
          className="dateTypeDropDown"
        >
          <Dropdown.Toggle
            as="div"
            className="filterInput customDropdownToggle"
            id="dropdown-date-type"
          >
            <span className="filterSpan">
              {selectedOption?.label || ""}
            </span>
            {isDateTypeOpen
              ? <ArrowDropUpIcon />
              : <ArrowDropDownIcon />}
          </Dropdown.Toggle>

          <Dropdown.Menu className="mt-1">
            {options.map((opt) => (
              <Dropdown.Item
                key={opt.key}
                id="dropdown-date-type-menu"
                onClick={() => {
                  setDateTypeKey(opt.key);
                  setIsDateTypeOpen(false);
                }}
                className={
                  selectedOption?.key === opt.key
                    ? "selectedDropdownItem p-2"
                    : "dropdownItem p-2"
                }
              >
                {opt.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Form.Group>
    );
  };

  return (
    <>
      {_.isEmpty(_.get(query, dashboardView, "")) ? null : (
        <div className="filterQueryDiv">
          <Dropdown>
            <Badge badgeContent={"✔"} classes={{ badge: "badge" }}>
              <Dropdown.Toggle
                as={Filter}
                style={{
                  cursor: "pointer",
                }}
                id="dropdown-viewFilters"
              />
            </Badge>
            <Dropdown.Menu id={"Menu_Notify"} className="filterChipDropdown">
              {_.map(
                FilterUtils.getMapValue(
                  _.get(query, dashboardView, ""),
                  filterList,
                  userRole,
                  dashboardView
                ),
                (value, key) => {
                  let isPresetFilter = _.includes(
                    ["DMO", "Encoding", "PACS", "My Drafts", "My Favorites", "My Team's Requests", "My Team's Jobs"],
                    _.get(value, "label")
                  );
                  return (
                    <div key={"Menu_List_dropDown" + key}>
                      <span key={"Menu" + key} className="filterChipDropdown">
                        {isPresetFilter
                          ? renderPresetFilter(_.get(value, "label"))
                          : _.get(value, "label")}
                      </span>
                      {_.has(value, "type") ? (
                        <li
                          className="filterChipList"
                          key={_.get(value, "label") + key}
                        >
                          {isPresetFilter ? null : (
                            <Chip
                              label={_.get(value, "value")}
                              key={"chip" + key}
                              size="small"
                              className="filterChipList"
                            />
                          )}
                        </li>
                      ) : (
                        _.map(_.get(value, "value", ""), (list, index) => {
                          return (
                            <li
                              className="filterChipList"
                              key={_.get(list, "label") + index}
                            >
                              <Chip
                                label={_.get(list, "label")}
                                key={index}
                                size="small"
                                className="filterChip"
                                clickable
                                onDelete={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deleteValue(
                                    _.get(list, "value"),
                                    _.get(value, "key")
                                  );
                                }}
                              />
                            </li>
                          );
                        })
                      )}
                    </div>
                  );
                }
              )}
              <hr />
              <Dropdown.Item
                as={StyledDarkBlueButton}
                className="applyButton"
                variant="contained"
                type="submit"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateFilter(
                    dateRange,
                    fromDate,
                    toDate,
                    searchText,
                    query,
                    dashboardView,
                    dashboardView === DashboardMode.JOBS ? jobDateType : requestDateType,
                    _setFilter,
                    dashboardView === DashboardMode.JOBS ? _setJobDateType : _setRequestDateType,
                    showLoading
                  );
                }}
                eventKey={"apply_button"}
              >
                Apply
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}
      <Container fluid className="filterContainer">
        <Container fluid className="m-0 p-0">
          <Form
            id={"filter_form"}
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Form.Row className="m-0 p-0">
              {userRole === 'cr_api' ? null : (
                <Tooltip
                  title={(
                    <>
                      <p className="searchToolTipTitle">Searchable fields include:</p>
                      <ul className="searchToolTipList">
                        {SearchFieldsMap[dashboardView].map(field => <li key={field}>{field}</li>)}
                      </ul>
                    </>
                  )}
                  interactive
                  arrow
                  placement="bottom"
                  classes={{ tooltip: classes.searchToolTip }}
                >
                  <Info className="infoIconStyle" />
                </Tooltip>
              )}
              <Form.Group
                as={Col}
                controlId="search"
                className="searchFilterFormGroup"
              >
                <Form.Control
                  type="text"
                  name="Search"
                  placeholder={"Search"}
                  autoComplete="off"
                  value={searchText}
                  bsPrefix="searchFilterInput"
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                />
              </Form.Group>
              <Dropdown
                onSelect={(eventKey, event) => {
                  handleClick(event, eventKey);
                }}
                className="dateDropdown"
              >
                <Tooltip
                  title={
                     "Click to customise date submitted results with start & end date"                      
                  }
                  interactive
                  placement="top"
                  arrow
                  classes={{ tooltip: "dateDropdownTooltip" }}
                >
                  <Dropdown.Toggle
                    as={AccessTimeIcon}
                    id="dropdown-custom-components"
                  ></Dropdown.Toggle>
                </Tooltip>
                <Dropdown.Menu className="mt-1">
                  <Dropdown.Item
                    className={
                      dateRange === "all" || _.isEmpty(dateRange)
                        ? "selectedDropdownItem p-2"
                        : "dropdownItem p-2"
                    }
                    eventKey={"all"}
                  >
                    All Dates
                  </Dropdown.Item>
                  <Dropdown.Item
                    eventKey={"lastFortyEight"}
                    className={
                      dateRange === "lastFortyEight"
                        ? "selectedDropdownItem p-2"
                        : "dropdownItem p-2"
                    }
                  >
                    Last 48 Hours
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={
                      dateRange === "lastWeek"
                        ? "selectedDropdownItem p-2"
                        : "dropdownItem p-2"
                    }
                    eventKey={"lastWeek"}
                  >
                    Last Week
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={
                      dateRange === "lastMonth"
                        ? "selectedDropdownItem p-2"
                        : "dropdownItem p-2"
                    }
                    eventKey={"lastMonth"}
                  >
                    Last Month
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={
                      dateRange === "lastYear"
                        ? "selectedDropdownItem p-2"
                        : "dropdownItem p-2"
                    }
                    eventKey={"lastYear"}
                  >
                    Last Year
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={"selectedDropdownItem p-2"}
                    eventKey={"custom"}
                  >
                    Custom
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <div className={`filterDiv${(userRole === 'cr_api' || userRole === 'mediarequest') && dateRange === "all" ? ' cr_api_filters' : ''}`}>
                {dateRange === "all" ? null : (
                  <>
                    {renderDateTypeDropdown(
                      DATE_TYPE_OPTIONS,
                      dashboardView,
                      dashboardView === DashboardMode.JOBS ? jobDateType : requestDateType,
                      dashboardView === DashboardMode.JOBS ? _setJobDateType : _setRequestDateType,
                      isDateTypeOpen,
                      setIsDateTypeOpen
                    )}
                    <Form.Group
                      controlId="fromDate"
                      as={Col}
                      className="dateRangeFilterFormGroup"
                    >
                      <Form.Control
                        bsPrefix="dateRangeInput"
                        type="date"
                        name="From Date"
                        value={fromDate}
                        onChange={(e) => {
                          e.preventDefault();
                          setDateRange("custom");
                          setFromDate(e.target.value);
                        }}
                      />
                      {/* </InputGroup> */}
                    </Form.Group>
                    <Form.Group
                      as={Col}
                      controlId="toDate"
                      className="dateRangeFilterFormGroup"
                    >
                      <Form.Control
                        bsPrefix="dateRangeInput"
                        type="date"
                        name="To Date"
                        value={toDate}
                        onChange={(e) => {
                          e.preventDefault();
                          setDateRange("custom");
                          setToDate(e.target.value);
                        }}
                      />
                    </Form.Group>
                  </>
                )}
                {filters}
              </div>
              <div>
                <StyledBlueButton
                  variant="contained"
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    updateFilter(
                      dateRange,
                      fromDate,
                      toDate,
                      searchText,
                      query,
                      dashboardView,
                      dashboardView === DashboardMode.JOBS ? jobDateType : requestDateType,
                      _setFilter,
                      dashboardView === DashboardMode.JOBS ? _setJobDateType : _setRequestDateType,
                      showLoading
                    );
                  }}
                >
                  Search
                </StyledBlueButton>
                <StyledBlueButton
                  variant="contained"
                  onClick={(e) => {
                    e.preventDefault();
                    showLoading();
                    _setFilter({});
                    //Reseting local states
                    setQuery({});
                    setDateRange("all");
                    setSearchText("");
                    _setJobDateType("SUBMITTED_DATE");
                    _setRequestDateType("SUBMITTED_DATE");
                  }}
                >
                  Clear Filters
                </StyledBlueButton>
              </div>
            </Form.Row>            
          </Form>
        </Container>
      </Container>
      {!_.isEmpty(view) ? (
        <PopoverFilter
          key={"filter_opt_" + _.get(view, "key", "")}
          filterItem={view}
          setView={setView}
          query={query}
          setQuery={(data) => {
            setQuery(data);
          }}
          getFilterByKey={getFilterByKey}
          classStyle={classes}
        />
      ) : null}
    </>
  );
};

export default React.memo(CustomFilters);
